import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import type { Message, Session } from 'talkjs/all';
import useIsInteragencyChatEnabled from '@rsos/hooks/useIsInteragencyChatEnabled';
import { useCurrentOrgName } from '@rsos/sinatra/src/selectors';
import { selectIsInteragencyChatBannerEnabled } from '@rsos/sinatra/src/selectors/selectInteragencyChatNotifications';
import {
  dismissToastByID,
  notifyInteragencyMessage,
} from '@rsos/utils/toastUtils';
import { useListInteragencyConversationsQuery } from '../reduxDataStore/interagency/interagencyAPI';
import { useMutedInteragencyConversationIds } from '../reduxDataStore/interagency/interagencyChatSubscriptions';
import { useSelectedInteragencyChat } from '../reduxDataStore/interagency/interagencySlice';

/**
 * Interagency TalkJS conversation ids are `interagency.<uuid>`; the prefix is
 * what distinguishes them from tcapi/admin_sms/tf911 conversations arriving on
 * the same session. Mirrors INTERAGENCY_CONVERSATION_PREFIX in the
 * text_to_911 service.
 */
export const INTERAGENCY_CONVERSATION_PREFIX = 'interagency.';

function toastIdFor(conversationId: string): string {
  return `interagency-toast-${conversationId}`;
}

interface UseInteragencyChatToastNotificationsArgs {
  /**
   * The TalkJS session already created by useTrackUnreads. TalkJS supports
   * multiple onMessage subscribers, so this hook adds its own rather than
   * threading through ConversationsContext.
   */
  session: Pick<Session, 'onMessage'> | null;
  /** Whether the Agency Chat queue tab is the active one. */
  isAgencyChatTabActive: boolean;
  /**
   * A function that opens the given conversation when the user clicks a
   * banner — switching to the Agency Chat queue tab and selecting the
   * conversation. Supplied by the caller because the queue tab is component
   * state there, not something this hook can reach through the store.
   */
  openConversation: (conversationId: string) => void;
}

/**
 * Shows a notification banner when a message arrives in a subscribed
 * interagency conversation. Subscription is opt-out, so this fires for every
 * conversation the user has not muted. The unread indicators are driven
 * separately (ConversationsContext) and are unaffected by any of this.
 *
 * Gates, each one an acceptance criterion:
 *  - interagency chat access (no listener otherwise)
 *  - the agency admin has Notification Banners on
 *  - the user has not muted the conversation
 *  - the message is someone else's, in an interagency conversation
 *  - the conversation is not already open in the Agency Chat tab
 */
function useInteragencyChatToastNotifications({
  session,
  isAgencyChatTabActive,
  openConversation,
}: UseInteragencyChatToastNotificationsArgs): void {
  const isChatEnabled = useIsInteragencyChatEnabled();
  const isBannerEnabledForAgency = useSelector(
    selectIsInteragencyChatBannerEnabled,
  );
  const mutedConversationIds = useMutedInteragencyConversationIds();
  const selectedInteragencyChat = useSelectedInteragencyChat();

  // The badge in the queue tab bar keeps this same query subscribed, so this
  // is a cache read, not a second fetch. A conversation can still be missing
  // from the list (first message in a conversation created after the last
  // refetch); the toast then falls back to a nameless title.
  const orgName = useCurrentOrgName();
  const { data: conversationList } = useListInteragencyConversationsQuery(
    { orgName: orgName ?? '' },
    { skip: !orgName || !isChatEnabled },
  );

  const handleMessage = useCallback(
    (message: Message) => {
      const conversationId = message.conversation.id;
      if (!conversationId.startsWith(INTERAGENCY_CONVERSATION_PREFIX)) return;
      // Admin off suppresses the banner regardless of subscription state.
      if (!isBannerEnabledForAgency) return;
      // Muting suppresses it regardless of the admin setting.
      if (mutedConversationIds.includes(conversationId)) return;
      if (message.isByMe) return;
      // Already reading the conversation — the message lands in the chatbox.
      if (isAgencyChatTabActive && selectedInteragencyChat === conversationId) {
        return;
      }

      const conversationName =
        conversationList?.conversations.find(
          c => c.conversation_id === conversationId,
        )?.conversation_name ?? null;

      notifyInteragencyMessage({
        conversationName,
        // Media and location messages have an empty body.
        messagePreview: message.body || 'New message',
        onOpen: () => openConversation(conversationId),
        options: { toastId: toastIdFor(conversationId) },
      });
    },
    [
      conversationList,
      isAgencyChatTabActive,
      isBannerEnabledForAgency,
      openConversation,
      selectedInteragencyChat,
      mutedConversationIds,
    ],
  );

  useEffect(() => {
    if (!session || !isChatEnabled) return;
    const subscription = session.onMessage(handleMessage);
    return () => subscription.unsubscribe();
  }, [session, isChatEnabled, handleMessage]);

  // Dismiss the banner when its conversation is opened by any means — the
  // toast's own click, the queue row, or a programmatic selection.
  useEffect(() => {
    if (selectedInteragencyChat) {
      dismissToastByID(toastIdFor(selectedInteragencyChat));
    }
  }, [selectedInteragencyChat]);
}

export default useInteragencyChatToastNotifications;
